import { App } from "@circle-react/apps/MainApp";
import { GDPRCookieBanner } from "@circle-react/components/GDPR/GDPRCookieBanner";
import { SvgSprite } from "@circle-react/components/shared/SvgSprite";
import { LazyTranscriptsPlayer } from "@circle-react/wrappers/LazyTranscriptsPlayer";
import React from "react";
import type { ComponentType } from "react";
import { createRoot } from "react-dom/client";
import type { Root } from "react-dom/client";

import { OnboardingWelcomePopup } from "@/react/components/shared/OnboardingWelcomePopup";

/**
 * Used on <%= react_component_tag "ComponentName" %> tags in ERB files.
 * Vanilla JS replacement for the former Stimulus react_mount_controller.
 */
const COMPONENT_MAP: Record<string, ComponentType<any>> = {
  App: App,
  GDPRCookieBanner,
  VideoPlayer: LazyTranscriptsPlayer,
  SvgSprite,
  OnboardingWelcomePopup,
};

const SELECTOR = '[data-controller="react-mount"]';
const roots = new WeakMap<HTMLElement, Root>();

const jsonParse = (value: unknown): Record<string, unknown> => {
  if (typeof value !== "string") {
    return {};
  }

  try {
    return JSON.parse(value);
  } catch {
    return {};
  }
};

function mountElement(element: HTMLElement): void {
  if (roots.has(element)) return;

  const { component: componentName, props: stringProps } = element.dataset;
  const props = jsonParse(stringProps);
  const Component = componentName ? COMPONENT_MAP[componentName] : null;

  if (!Component) {
    console.error(`Error: invalid component - ${componentName}`);
    return;
  }

  const root = createRoot(element);
  root.render(
    React.createElement(
      React.StrictMode,
      null,
      React.createElement(Component, props),
    ),
  );
  roots.set(element, root);
}

function unmountElement(element: HTMLElement): void {
  const root = roots.get(element);
  if (root) {
    root.unmount();
    roots.delete(element);
  }
}

function mountAll(): void {
  document.querySelectorAll<HTMLElement>(SELECTOR).forEach(mountElement);
}

function processAddedNode(node: Node): void {
  if (!(node instanceof HTMLElement)) return;
  if (node.matches(SELECTOR)) mountElement(node);
  node.querySelectorAll<HTMLElement>(SELECTOR).forEach(mountElement);
}

function processRemovedNode(node: Node): void {
  if (!(node instanceof HTMLElement)) return;
  if (node.matches(SELECTOR)) unmountElement(node);
  node.querySelectorAll<HTMLElement>(SELECTOR).forEach(unmountElement);
}

// Observe the DOM for dynamically added/removed react-mount elements.
const observer = new MutationObserver(mutations => {
  for (const mutation of mutations) {
    mutation.addedNodes.forEach(processAddedNode);
    mutation.removedNodes.forEach(processRemovedNode);
  }
});

function startObserver(): void {
  mountAll();
  observer.observe(document.body, { childList: true, subtree: true });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", startObserver);
} else {
  startObserver();
}
